<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { usePlanningApi } from '../composables/usePlanningApi'
import type { ProductionOrder } from '../types/planning'

/**
 * Controlled rescheduling on real orders.
 *
 * Dates are written back with `PUT /production-orders/{id}`. The backend has no
 * schedule-version model, so there is nothing to snapshot: the guardrails here
 * are the checks the UI can verify (order state, date order, past dates) plus
 * the confirmation the backend does not require.
 */

const { t } = useI18n()
const auth = useAuthStore()
const { listProductionOrders, updateProductionOrder } = usePlanningApi()

const today = new Date().toISOString().slice(0, 10)

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('production_orders.manage'))

const orders = ref<ProductionOrder[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref<number | null>(null)
const drafts = ref<Record<string, { start_date: string; end_date: string; notes: string }>>({})

const editableOrders = computed(() =>
  orders.value.filter((order) => order.status === 'planned' || order.status === 'in_progress'),
)

const blockedOrders = computed(() =>
  orders.value.filter((order) => order.status === 'completed' || order.status === 'cancelled'),
)

function draftFor(order: ProductionOrder) {
  return (
    drafts.value[String(order.id)] ?? {
      start_date: order.start_date ?? '',
      end_date: order.end_date ?? '',
      notes: order.notes ?? '',
    }
  )
}

function setDraft(order: ProductionOrder, patch: Partial<{ start_date: string; end_date: string; notes: string }>) {
  drafts.value = { ...drafts.value, [String(order.id)]: { ...draftFor(order), ...patch } }
}

/** Guardrails the UI can check before writing. */
function guardrail(order: ProductionOrder): string {
  const draft = draftFor(order)
  if (draft.start_date && draft.end_date && draft.end_date < draft.start_date) {
    return t('planning.rescheduling.guardrailDates')
  }
  if (draft.end_date && draft.end_date < today) {
    return t('planning.rescheduling.guardrailPast')
  }
  return ''
}

function isDirty(order: ProductionOrder): boolean {
  const draft = draftFor(order)
  return (
    draft.start_date !== (order.start_date ?? '') ||
    draft.end_date !== (order.end_date ?? '') ||
    draft.notes !== (order.notes ?? '')
  )
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listProductionOrders({ page: 1, per_page: 100 })
    orders.value = result.items
    drafts.value = {}
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function save(order: ProductionOrder) {
  actionError.value = ''
  actionMessage.value = ''
  const blocked = guardrail(order)
  if (blocked) {
    actionError.value = blocked
    return
  }
  busyId.value = order.id
  const draft = draftFor(order)
  try {
    await updateProductionOrder(order.id, {
      start_date: draft.start_date || null,
      end_date: draft.end_date || null,
      notes: draft.notes.trim() || undefined,
    })
    actionMessage.value = t('planning.rescheduling.saved', { code: order.mo_code })
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('planning.eyebrow') }}</p>
      <h2>{{ t('planning.rescheduling.title') }}</h2>
      <p>{{ t('planning.rescheduling.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('planning.common.refresh') }}
    </button>
    <span class="spacer" />
    <div class="summary">{{ t('planning.rescheduling.editHint') }}</div>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('planning.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('planning.common.retry') }}</button>
  </div>

  <template v-else>
    <section v-if="editableOrders.length === 0" class="state-message">
      <p>{{ t('planning.rescheduling.noOrders') }}</p>
    </section>

    <section v-else class="table-shell">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('planning.common.order') }}</th>
            <th>{{ t('planning.common.product') }}</th>
            <th>{{ t('planning.common.status') }}</th>
            <th>{{ t('planning.rescheduling.currentWindow') }}</th>
            <th>{{ t('planning.rescheduling.newStart') }}</th>
            <th>{{ t('planning.rescheduling.newEnd') }}</th>
            <th>{{ t('planning.common.notes') }}</th>
            <th class="cell-actions">{{ t('planning.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in editableOrders" :key="order.id">
            <td class="cell-code">{{ order.mo_code }}</td>
            <td class="cell-name">{{ order.product_name || '—' }}</td>
            <td>
              <span
                class="status-badge"
                :class="order.status === 'in_progress' ? 'status-info' : 'status-neutral'"
              >
                {{ t(`planning.orderStatus.${order.status}`) }}
              </span>
            </td>
            <td class="cell-date">
              {{ formatDisplayDate(order.start_date) || '—' }} –
              {{ formatDisplayDate(order.end_date) || '—' }}
            </td>
            <td>
              <input
                :value="draftFor(order).start_date"
                class="date-input"
                type="date"
                :disabled="!canManage"
                @input="setDraft(order, { start_date: ($event.target as HTMLInputElement).value })"
              >
            </td>
            <td>
              <input
                :value="draftFor(order).end_date"
                class="date-input"
                type="date"
                :disabled="!canManage"
                @input="setDraft(order, { end_date: ($event.target as HTMLInputElement).value })"
              >
            </td>
            <td>
              <input
                :value="draftFor(order).notes"
                class="search-input"
                type="text"
                :disabled="!canManage"
                @input="setDraft(order, { notes: ($event.target as HTMLInputElement).value })"
              >
            </td>
            <td class="cell-actions">
              <span v-if="guardrail(order)" class="status-badge status-critical">
                {{ guardrail(order) }}
              </span>
              <button
                v-else-if="canManage"
                class="link-action"
                :disabled="busyId === order.id || !isDirty(order)"
                @click="save(order)"
              >
                {{ t('planning.rescheduling.save') }}
              </button>
              <span v-else class="empty-inline">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-if="blockedOrders.length" class="notice-panel">
      <strong>{{ t('planning.rescheduling.blockedStatus') }}</strong>
      <span>
        {{ t('planning.rescheduling.blockedText', { count: blockedOrders.length }) }}
      </span>
    </section>

    <p class="empty-inline">{{ t('planning.rescheduling.guardrailText') }}</p>
  </template>
</template>
